import {
  Injectable,
  ComponentFactoryResolver,
  Injector,
  EmbeddedViewRef,
  ApplicationRef,
} from '@angular/core';
import { Subject, Subscription, timer } from 'rxjs';
import { AuthService } from '../../app/services/auth.service';
import { environment } from '../../environments/environment';
import { MicarteraService } from './micartera.service';
import { ModalInactividadComponent } from '../helpers/modal-inactividad.component';

@Injectable({
  providedIn: 'root',
})
export class InactivityService {
  private userActivity: Subject<void> = new Subject();
  private inactivityTimerSubscription: Subscription = new Subscription();
  private modalTimerSubscription: Subscription = new Subscription();
  private inactivityTimeLimit =
    Number(environment.inactivity_time_limit_in_minutes) * 60 * 1000;
  private modalTimeLimit =
    Number(environment.out_of_inactivity_time_limit_in_minutes) * 60 * 1000;
  private modalRef: any = null; // Para mantener referencia al modal actual

  constructor(
    private authService: AuthService,
    private micarteraService: MicarteraService,
    private componentFactoryResolver: ComponentFactoryResolver,
    private injector: Injector,
    private appRef: ApplicationRef
  ) {
    this.resetTimer();
    this.userActivity.subscribe(() => this.resetTimer());
  }

  registerUserActivity() {
    this.userActivity.next();
  }

  private resetTimer() {
    if (this.inactivityTimerSubscription) {
      this.inactivityTimerSubscription.unsubscribe();
    }
    this.inactivityTimerSubscription = timer(
      this.inactivityTimeLimit
    ).subscribe(() => {
      if (this.authService.isAuthenticated() && !this.modalRef) {
        this.promptLogout();
      }
    });
  }

  private async promptLogout() {
    if (this.modalRef) {
      return;
    }

    this.modalRef = this.componentFactoryResolver
      .resolveComponentFactory(ModalInactividadComponent)
      .create(this.injector);

    this.appRef.attachView(this.modalRef.hostView);
    const domElem = (this.modalRef.hostView as EmbeddedViewRef<any>)
      .rootNodes[0];
    document.body.appendChild(domElem);

    this.modalRef.instance.show();

    // Iniciar temporizador del modal
    this.startModalTimer();

    // Esperar la respuesta del usuario
    const confirmed = await new Promise<boolean>((resolve) => {
      this.modalRef.instance.onConfirm = () => {
        this.clearModalTimer();
        resolve(true);
      };
      this.modalRef.instance.onCancel = () => {
        this.clearModalTimer();
        resolve(false);
      };
    });

    this.cleanupModal();

    if (confirmed) {
      this.resetTimer();
    } else {
      this.forceLogout();
    }
  }

  private startModalTimer() {
    if (this.modalTimerSubscription) {
      this.modalTimerSubscription.unsubscribe();
    }
    this.modalTimerSubscription = timer(this.modalTimeLimit).subscribe(() => {
      this.forceLogout();
    });
  }

  private clearModalTimer() {
    if (this.modalTimerSubscription) {
      this.modalTimerSubscription.unsubscribe();
    }
  }

  private cleanupModal() {
    if (this.modalRef) {
      this.appRef.detachView(this.modalRef.hostView);
      this.modalRef.destroy();
      this.modalRef = null;
    }
  }

  private forceLogout() {
    this.cleanupModal();
    this.authService.signOut();
    this.micarteraService.clearCache();
  }

  ngOnDestroy() {
    this.clearModalTimer();
    if (this.inactivityTimerSubscription) {
      this.inactivityTimerSubscription.unsubscribe();
    }
  }
}
